// src/App.tsx - CORRECTED: Uses existing NavigationHeader component and UnifiedNavigation
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { authHeaders } from '../../services/authHeaders';
// Removed unused imports: Download, Plus

// ✅ FIXED: Import correct interfaces and services
import { 
  defensePlanningBusinessService, 
  BusinessIntelligenceData  // ✅ FIXED: Use correct interface
} from '../../services/businessIntelligenceService';

// Import shared types
import {
  UserType,
  AttackVectorWithControls,
  ReadinessZone,
  IntegrationData,
  SpiderChartData,
  AppConfig,
  RealAssessmentData
} from './types';

// Import extracted components
import HowItWorksGuide from '../../shared/components/HowItWorksGuide';
import BusinessIntelligenceStatus from './components/BusinessIntelligenceStatus';
import SpiderChart from './components/SpiderChart';
import AttackVectorsList from './components/AttackVectorsList';
import BusinessImpactAnalysis from './components/BusinessImpactAnalysis';
import GoldilocksZones from './components/GoldilocksZones';
import ReadinessSliders from './components/ReadinessSliders';
import RecommendationOptimizer from './components/RecommendationOptimizer';
// RiskIntelligenceCenter removed — replaced by Financial Impact Analysis (ExecutiveSimulationStudio)
import OutsidePressuresCenter from './components/OutsidePressuresCenter'; // ✅ Outside Pressures Analysis
import ExecutiveSimulationStudio from './components/ExecutiveSimulationStudio';
import BoardReportGenerator from './components/BoardReportGenerator'; // ✅ Board reporting
// Removed unused import: NavigationHeader
// import UnifiedNavigation from './components/UnifiedNavigation'; // ✅ Removed - handled by AppLayout wrapper

// ============ UTILITY FUNCTIONS ============

// Explicit null/undefined checks only — never `||`/`??` over a numeric measurement.
// A genuine $0 reported revenue must survive as 0, distinct from "not measured".
const resolveAnnualRevenue = (
  executiveData: { annualRevenue?: number | null } | null | undefined,
  businessData: BusinessIntelligenceData | null
): number | null => {
  const fromExecutive = executiveData?.annualRevenue;
  if (fromExecutive !== null && fromExecutive !== undefined) return fromExecutive;
  const fromBusiness = businessData?.companyProfile?.revenue;
  if (fromBusiness !== null && fromBusiness !== undefined) return fromBusiness;
  return null;
};

// Detect persona from URL parameters or session storage
const detectCurrentPersona = (): { persona: string, personaDisplayName: string } => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlPersona = urlParams.get('persona');
  const sessionPersona = sessionStorage.getItem('currentPersona');
  const currentPersona = urlPersona || sessionPersona || '';
  
  // Map persona codes to display names
  const personaMapping: { [key: string]: string } = {
    'c-level': 'C-Level Executive',
    'c_level': 'C-Level Executive',
    'ceo': 'CEO',
    'cto': 'CTO', 
    'cfo': 'CFO',
    'it': 'IT Manager',
    'it_manager': 'IT Manager',
    'it-manager': 'IT Manager',
    'hr': 'HR Director',
    'hr_director': 'HR Director', 
    'hr-director': 'HR Director',
    'operations': 'Operations Manager',
    'ops': 'Operations Manager',
    'security': 'Security Officer',
    'security_officer': 'Security Officer',
    'compliance': 'Compliance Officer',
    'legal': 'Legal Counsel'
  };
  
  const personaDisplayName = personaMapping[currentPersona.toLowerCase()] || 
    (currentPersona ? currentPersona.replace(/[-_]/g, ' ').replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : '');
  
  return {
    persona: currentPersona,
    personaDisplayName
  };
};

// Simple production detection and API URL override
const getApiBaseUrl = (): string => {
  // Simple check: if we're on the production domain, use relative URLs (nginx will handle proxy)
  if (window.location.hostname.includes('insights-risk-aperture.com')) {
    return ''; // Use relative URLs, nginx will proxy /api/* to backend
  }
  
  // Development fallback
  return import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001' : '');
};

const API_BASE_URL = getApiBaseUrl();
console.log('🔗 API Base URL configured:', API_BASE_URL);

// ============ READINESS ZONE CALCULATION ============

export const calculateReadinessZones = (currentScores: { [key: string]: number }, businessData: BusinessIntelligenceData | null = null, industry: string = 'General'): ReadinessZone[] => {
  // Industry-specific Goldilocks zone ranges
  // Regulated industries (Finance, Healthcare) have tighter optimal ranges pushed higher
  // Less mature industries (Retail, Education, Real Estate) have wider, lower ranges
  const industryProfiles: { [key: string]: { [cat: string]: { standard: number; bestInClass: number } } } = {
    Finance:       { governance: { standard: 90, bestInClass: 75 }, culture: { standard: 90, bestInClass: 78 }, budget: { standard: 95, bestInClass: 82 }, technical_debt: { standard: 95, bestInClass: 85 }, third_party: { standard: 88, bestInClass: 72 }, people: { standard: 92, bestInClass: 78 } },
    Healthcare:    { governance: { standard: 88, bestInClass: 72 }, culture: { standard: 88, bestInClass: 75 }, budget: { standard: 92, bestInClass: 80 }, technical_debt: { standard: 92, bestInClass: 82 }, third_party: { standard: 85, bestInClass: 70 }, people: { standard: 90, bestInClass: 75 } },
    Technology:    { governance: { standard: 85, bestInClass: 70 }, culture: { standard: 90, bestInClass: 75 }, budget: { standard: 92, bestInClass: 78 }, technical_debt: { standard: 98, bestInClass: 85 }, third_party: { standard: 82, bestInClass: 68 }, people: { standard: 88, bestInClass: 72 } },
    Manufacturing: { governance: { standard: 85, bestInClass: 70 }, culture: { standard: 90, bestInClass: 75 }, budget: { standard: 95, bestInClass: 80 }, technical_debt: { standard: 100, bestInClass: 85 }, third_party: { standard: 80, bestInClass: 65 }, people: { standard: 88, bestInClass: 72 } },
    Energy:        { governance: { standard: 87, bestInClass: 72 }, culture: { standard: 88, bestInClass: 74 }, budget: { standard: 93, bestInClass: 80 }, technical_debt: { standard: 95, bestInClass: 83 }, third_party: { standard: 83, bestInClass: 68 }, people: { standard: 88, bestInClass: 73 } },
    Retail:        { governance: { standard: 80, bestInClass: 62 }, culture: { standard: 82, bestInClass: 65 }, budget: { standard: 88, bestInClass: 72 }, technical_debt: { standard: 90, bestInClass: 78 }, third_party: { standard: 78, bestInClass: 60 }, people: { standard: 82, bestInClass: 65 } },
    Education:     { governance: { standard: 78, bestInClass: 60 }, culture: { standard: 80, bestInClass: 62 }, budget: { standard: 85, bestInClass: 68 }, technical_debt: { standard: 88, bestInClass: 75 }, third_party: { standard: 75, bestInClass: 58 }, people: { standard: 80, bestInClass: 62 } },
    'Real Estate': { governance: { standard: 76, bestInClass: 58 }, culture: { standard: 78, bestInClass: 60 }, budget: { standard: 84, bestInClass: 66 }, technical_debt: { standard: 86, bestInClass: 72 }, third_party: { standard: 74, bestInClass: 56 }, people: { standard: 78, bestInClass: 60 } },
    Transportation:{ governance: { standard: 82, bestInClass: 65 }, culture: { standard: 84, bestInClass: 68 }, budget: { standard: 90, bestInClass: 75 }, technical_debt: { standard: 92, bestInClass: 80 }, third_party: { standard: 80, bestInClass: 62 }, people: { standard: 85, bestInClass: 68 } }
  };

  // Default ranges for unrecognized industries
  const defaultProfile = { governance: { standard: 82, bestInClass: 65 }, culture: { standard: 85, bestInClass: 68 }, budget: { standard: 90, bestInClass: 75 }, technical_debt: { standard: 92, bestInClass: 80 }, third_party: { standard: 78, bestInClass: 62 }, people: { standard: 85, bestInClass: 68 } };

  const industryBenchmarks = industryProfiles[industry] || defaultProfile;
  
  // ✅ FIXED: Use corrected business data for friction calculation
  const frictionData = businessData ?
    defensePlanningBusinessService.calculateFrictionMultipliers(businessData) :
    null;

  // Two distinct zero-states, kept distinguishable:
  //   1. No business data at all (frictionData === null) — nothing to compute from.
  //      Existing behaviour ($0/year) is kept as-is; this is a pre-existing, deliberate
  //      simplification (see the original comment below), not the defect being fixed here.
  //   2. Business data exists but revenue specifically could not be measured
  //      (frictionData.revenueUnmeasured === true) — THIS is the headline defect:
  //      it must never render as "$0/year", which reads as a finding ("no friction to
  //      fix here") rather than as missing data.
  const revenueUnmeasured = frictionData?.revenueUnmeasured === true;

  const getMultiplier = (category: string, type: 'underOptimal' | 'overOptimal'): number => {
    if (frictionData?.multipliers?.[category as keyof typeof frictionData.multipliers]?.[type]) {
      return frictionData.multipliers[category as keyof typeof frictionData.multipliers][type];
    }

    // No business data, or revenue unmeasured — 0 keeps the arithmetic safe.
    // The unmeasured case is labeled via `revenueUnmeasured` below; never inferred
    // from this 0, which is also the genuine "no business data" value.
    return 0;
  };

  // Helper to create zone with proper friction calculations
  const createZone = (
    name: string,
    categoryKey: string,
    current: number,
    benchmark: { standard: number, bestInClass: number }
  ): ReadinessZone => {
    const frictionAmount = current > benchmark.standard ?
      (current - benchmark.standard) * getMultiplier(categoryKey, 'underOptimal') :
      current < benchmark.bestInClass ?
      (benchmark.bestInClass - current) * getMultiplier(categoryKey, 'overOptimal') :
      0;

    return {
      name,
      current,
      optimal: `${benchmark.bestInClass}-${benchmark.standard}`,
      optimalMin: benchmark.bestInClass,
      optimalMax: benchmark.standard,
      industryStandard: benchmark.standard,
      bestInClass: benchmark.bestInClass,
      friction: revenueUnmeasured ? 'Not measured' : (frictionAmount > 0 ? `$${frictionAmount.toLocaleString()}/year` : '$0/year'),
      frictionAmount,
      // Consumers (GoldilocksZones, BusinessImpactAnalysis) must check this before
      // summing or displaying frictionAmount as a dollar figure — frictionAmount stays
      // a real 0 here only so arithmetic elsewhere doesn't break; it is not a measurement.
      frictionUnmeasured: revenueUnmeasured,
      isInOptimal: current >= benchmark.bestInClass && current <= benchmark.standard
    };
  };

  const zones: ReadinessZone[] = [
    createZone('Governance', 'governance', currentScores.governance, industryBenchmarks.governance),
    createZone('Culture', 'culture', currentScores.culture, industryBenchmarks.culture),
    createZone('Budget', 'budget', currentScores.budget, industryBenchmarks.budget),
    createZone('Technical Debt', 'technical_debt', currentScores.technical_debt || currentScores.technicalDebt, industryBenchmarks.technical_debt),
    createZone('Third Party', 'third_party', currentScores.third_party || currentScores.thirdParty, industryBenchmarks.third_party),
    createZone('People', 'people', currentScores.people, industryBenchmarks.people)
  ];

  return zones;
};

// ============ REAL ASSESSMENT DATA FETCHING ============

const fetchRealAssessmentData = async (assessmentId: number): Promise<{ adapted: RealAssessmentData; raw: any } | null> => {
  try {
    const possibleEndpoints = [
      `${API_BASE_URL}/api/business-intelligence/${assessmentId}`,
      `${API_BASE_URL}/api/defense-analytics/${assessmentId}`
    ];

    for (const endpoint of possibleEndpoints) {
      try {
        // Was reading ONLY 'token', but a user arriving from Insights carries the credential
        // as 'defenseAuthToken' (stashed from the ?token= param) — so the documented entry
        // path could send nothing at all. authHeaders() checks all three keys in order.
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
          }
        });

        if (response.ok) {
          const data = await response.json();

          // Handle different response formats from various endpoints
          let rawSource = data;
          if (data.success && data.assessmentId) {
            return { adapted: adaptAssessmentData(data, assessmentId), raw: data };
          } else if (data.success && data.data) {
            return { adapted: adaptAssessmentData(data.data, assessmentId), raw: data.data };
          } else if (data.assessment_id || data.company_name || data.companyName) {
            return { adapted: adaptAssessmentData(data, assessmentId), raw: data };
          } else if (data.latestAssessment) {
            return { adapted: adaptAssessmentData(data.latestAssessment, assessmentId), raw: data.latestAssessment };
          }
        }
      } catch (endpointError) {
        continue;
      }
    }

    console.warn('⚠️ All assessment endpoints failed, using fallback');
    return null;

  } catch (error) {
    console.error('❌ Error fetching real assessment data:', error);
    return null;
  }
};

// requestedId: the session this data was fetched FOR — the id to fall back to, never a constant.
const adaptAssessmentData = (rawData: any, requestedId: number): RealAssessmentData => {
  console.log('🔄 Adapting assessment data:', rawData);
  console.log('🔍 rawData keys:', Object.keys(rawData));
  console.log('🔍 rawData.currentReadiness:', rawData.currentReadiness);
  console.log('🔍 rawData.categoryScores:', rawData.categoryScores);
  console.log('🔍 rawData.enhancedCategories:', rawData.enhancedCategories);
  
  // FIXED: Calculate Big 6 scores from 360 data if needed
  let big6Scores = {
    governance: 35,
    technicalDebt: 35,
    culture: 35,
    thirdParty: 35,
    budget: 35,
    people: 35
  };
  
  // First try to get from categoryScores (if backend calculated them)
  if (rawData.categoryScores && Object.keys(rawData.categoryScores).length > 0) {
    console.log('📊 Using categoryScores from backend:', rawData.categoryScores);
    big6Scores = {
      governance: rawData.categoryScores.governance || 35,
      technicalDebt: rawData.categoryScores.technicalDebt || rawData.categoryScores.technical_debt || 35,
      culture: rawData.categoryScores.culture || 35,
      thirdParty: rawData.categoryScores.thirdParty || rawData.categoryScores.third_party || 35,
      budget: rawData.categoryScores.budget || 35,
      people: rawData.categoryScores.people || 35
    };
  } 
  // Otherwise calculate from 360 scores if available
  else if (rawData.enhancedCategories && typeof rawData.enhancedCategories === 'object') {
    console.log('📊 Calculating Big 6 from 360 scores...');
    
    // Map 360 scores to Big 6 categories (simplified mapping)
    // In reality, this would need more complex mapping based on business rules
    const e360 = rawData.enhancedCategories.enterprise360;
    const d360 = rawData.enhancedCategories.data360;
    const c360 = rawData.enhancedCategories.cyber360;
    const p360 = rawData.enhancedCategories.people360;
    
    if (e360 || d360 || c360 || p360) {
      // Enterprise360 maps to Governance and Technical Debt
      if (e360) {
        const instDebt = e360.components?.institutionalDebt?.averageScore || 0;
        const techDebt = e360.components?.technicalDebt?.averageScore || 0;
        big6Scores.governance = Math.round(instDebt) || 35;
        big6Scores.technicalDebt = Math.round(techDebt) || 35;
      }
      
      // Cyber360 maps to Third Party and part of Technical Debt
      if (c360) {
        big6Scores.thirdParty = Math.round(c360.averageScore || c360.totalScore || 0) || 35;
      }
      
      // People360 maps to People and Culture
      if (p360) {
        const peopleScore = Math.round(p360.averageScore || p360.totalScore || 0);
        big6Scores.people = peopleScore || 35;
        big6Scores.culture = peopleScore || 35; // Culture is closely related to People
      }
      
      // Data360 influences Budget (data protection costs)
      if (d360) {
        big6Scores.budget = Math.round(d360.averageScore || d360.totalScore || 0) || 35;
      }
      
      console.log('✅ Calculated Big 6 scores from 360 data:', big6Scores);
    }
  }
  
  const adapted: RealAssessmentData = {
    assessment_id: rawData.assessmentId || rawData.assessment_id || rawData.id || requestedId,
    company_name: rawData.companyName || rawData.company_name || 'Unknown Company',
    industry: rawData.industry || 'Technology',
    completed_at: rawData.completed_at || rawData.completedAt || new Date().toISOString(),
    overallRiskScore: {
      score: rawData.riskScore || rawData.overallRiskScore?.score || rawData.overall_risk_score || 35,
      riskLevel: rawData.overallRiskScore?.riskLevel || rawData.risk_level || 'Medium'
    },
    enhancedCategories: big6Scores,
    threatAnalysis: {
      vectors: rawData.industryBenchmarks?.threatLandscape?.map((threat: string, index: number) => ({
        id: index + 1,
        name: threat,
        category: (rawData.industryBenchmarks?.industryName || 'Industry') + ' Threat',
        likelihood: Math.max(3, 5 - index),   // Deterministic: descending 5,4,3,3,3...
        impact: Math.max(3, 5 - Math.floor(index / 2)), // Deterministic: 5,5,4,4,3,3...
        risk_score: Math.max(12, 25 - index * 3) // Deterministic: 25,22,19,16,13,12...
      })) || rawData.defenseOptimization?.recommendedActions?.map((action: any, index: number) => ({
        id: index + 1,
        name: `${action.category.toUpperCase()}: ${action.action}`,
        category: action.urgency + ' Priority',
        likelihood: action.urgency === 'Critical' ? 5 : action.urgency === 'High' ? 4 : 3,
        impact: 4,
        risk_score: action.urgency === 'Critical' ? 25 : action.urgency === 'High' ? 20 : 15
      })) || rawData.threatAnalysis?.vectors || rawData.threat_analysis?.vectors || rawData.attackVectors || []
    }
  };
  
  console.log('✅ Adapted data:', adapted);
  return adapted;
};

// ============ MAIN APP COMPONENT ============

const App: React.FC = () => {
  const location = useLocation(); // Add useLocation hook for navigation detection
  // ✅ FIXED: Use correct interface
  const [integrationData, setIntegrationData] = useState<IntegrationData | null>(null);
  // const [isIntegratedMode, setIsIntegratedMode] = useState<boolean>(false); // Unused for now
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // ✅ FIXED: Use BusinessIntelligenceData instead of BusinessIntelligenceResponse
  const [businessData, setBusinessData] = useState<BusinessIntelligenceData | null>(null);
  const [businessLoading, setBusinessLoading] = useState<boolean>(true);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [isFallbackData, setIsFallbackData] = useState<boolean>(false);
  
  // 🎯 NEW: Dual-score model to prevent feedback loops
  const [asIsScores, setAsIsScores] = useState<{ [key: string]: number } | null>(null);   // Immutable baseline
  const [toBeScores, setToBeScores] = useState<{ [key: string]: number } | null>(null);   // Modified by recommendations
  
  // 💰 NEW: Executive financial data from database
  const [executiveData, setExecutiveData] = useState<{
    industry: string | null;
    annualRevenue: number | null;
    currentCyberBudget: number | null;
  } | null>(null);

  // Cache raw BI API response to avoid duplicate fetches
  const cachedBiResponseRef = useRef<any>(null);
  
  // const [user, setUser] = useState<UserType>({ // Unused for now
  //   id: '1',
  //   name: 'Business User',
  //   email: 'user@company.com',
  //   firstName: 'Business',
  //   lastName: 'User'
  // });
  
  const [selectedVector, setSelectedVector] = useState<AttackVectorWithControls | null>(null);
  // Removed vectorControls - no longer needed without SecurityControls
  
  // View state based on URL parameters
  const [currentView, setCurrentView] = useState<'friction' | 'outside-pressures' | 'executive-simulation'>('friction');

  // ============ URL PARAMETER DETECTION ============
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlView = urlParams.get('view');
    
    // Also detect view from URL path
    const pathname = window.location.pathname;
    let pathView = 'friction'; // default
    
    if (pathname.includes('/studio/executive')) {
      pathView = 'executive-simulation';  
    // } else if (pathname.includes('/studio/scenarios')) {
    //   pathView = 'attack-vectors';
    // } else if (pathname.includes('/studio/optimization')) {
    //   pathView = 'goldilocks';
    // } else if (pathname.includes('/studio/impact')) {
    //   pathView = 'analytics';
    } else if (pathname.includes('/studio')) {
      pathView = 'friction';
    }
    
    const view = urlView || pathView;
    console.log('🔍 URL Detection - Current URL:', window.location.href);
    console.log('🔍 URL Detection - Path view:', pathView, 'URL view:', urlView, 'Final view:', view);
    setCurrentView(view as any);
  }, [location]); // Add location dependency to trigger on navigation

  // Listen for URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlView = urlParams.get('view');
      
      // Also detect view from URL path
      const pathname = window.location.pathname;
      let pathView = 'friction'; // default
      
      if (pathname.includes('/studio/intelligence')) {
        pathView = 'intelligence';
      } else if (pathname.includes('/studio/executive')) {
        pathView = 'executive-simulation';  
      // } else if (pathname.includes('/studio/scenarios')) {
      //   pathView = 'attack-vectors';
      // } else if (pathname.includes('/studio/optimization')) {
      //   pathView = 'goldilocks';
      // } else if (pathname.includes('/studio/impact')) {
      //   pathView = 'analytics';
      } else if (pathname.includes('/studio')) {
        pathView = 'friction';
      }
      
      const view = urlView || pathView;
      console.log('🔄 URL Changed - Path view:', pathView, 'URL view:', urlView, 'New view:', view);
      setCurrentView(view as any);
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [location]); // Add location dependency to trigger on React Router navigation

  // ============ DATA LOADING FUNCTIONS ============

  const loadRealAssessmentData = async (assessmentId: number): Promise<IntegrationData> => {
    const result = await fetchRealAssessmentData(assessmentId);

    if (result) {
      const realData = result.adapted;
      // Cache the raw BI response so loadBusinessIntelligence can reuse it
      cachedBiResponseRef.current = result.raw;
      
      // These are 360 friction scores — position on a spectrum, NOT linear good/bad.
      // Pass raw to Goldilocks zone calculator which compares against industry bands.
      const rawBig6Scores = realData.enhancedCategories;
      console.log('📊 Company Big 6 friction scores (Goldilocks input):', rawBig6Scores);
      
      const attackVectors: AttackVectorWithControls[] = realData.threatAnalysis.vectors.map((vector: any, index: number) => ({
        ...vector,
        current_risk_score: vector.risk_score,
        original_risk_score: vector.risk_score,
        id: vector.id || index + 1
      }));
      
      const sortedVectors = attackVectors.sort((a, b) => b.current_risk_score - a.current_risk_score);
      
      // ✅ Detect current persona for display
      const { persona, personaDisplayName } = detectCurrentPersona();
      
      const integrationData: IntegrationData = {
        assessmentId: realData.assessment_id,
        companyName: realData.company_name,
        industry: realData.industry,
        totalEmployees: 500,
        persona,  // Add persona information
        personaDisplayName,  // Add display name
        originalScores: rawBig6Scores,
        currentScores: rawBig6Scores,
        originalOverallRisk: realData.overallRiskScore,
        attackVectors: sortedVectors.slice(0, 5),
        allAttackVectors: sortedVectors,
        isSimulation: false,
        lastSimulationUpdate: null,
        dataSources: {
          originalData: 'Real Assessment Data from Database',
          simulationData: 'Readiness Optimization Engine',
          attackVectors: 'Real Threat Analysis from Assessment',
          assessmentType: 'Production Assessment with Real Data'
        }
      };
      
      
      return integrationData;
      
    } else {
      console.warn('⚠️ Could not load real data, using enhanced fallback');
      
      // Raw friction scores — pass directly to Goldilocks zone calculator
      const fallbackScores = {
        governance: 41, technicalDebt: 37, culture: 23,
        thirdParty: 13, budget: 22, people: 33
      };

      const fallbackVectors: AttackVectorWithControls[] = [
        { id: 1, name: 'Phishing and Social Engineering', likelihood: 3, impact: 2, risk_score: 19, current_risk_score: 19, category: 'Social Engineering' },
        { id: 2, name: 'Ransomware Attacks', likelihood: 2, impact: 4, risk_score: 24, current_risk_score: 24, category: 'Malware' },
        { id: 3, name: 'Insider Threats', likelihood: 2, impact: 3, risk_score: 13, current_risk_score: 13, category: 'Internal' },
        { id: 4, name: 'Third-Party Vendor Risks', likelihood: 2, impact: 2, risk_score: 14, current_risk_score: 14, category: 'Third Party' },
        { id: 5, name: 'Cloud Security Vulnerabilities', likelihood: 2, impact: 3, risk_score: 19, current_risk_score: 19, category: 'Infrastructure' }
      ];
      
      return {
        assessmentId: assessmentId,
        companyName: 'CaC_CUST',
        industry: 'Manufacturing',
        totalEmployees: 500,
        originalScores: fallbackScores,
        currentScores: fallbackScores,
        originalOverallRisk: { score: 28, riskLevel: 'Low-Moderate' },
        attackVectors: fallbackVectors,
        allAttackVectors: fallbackVectors,
        isSimulation: false,
        lastSimulationUpdate: null,
        dataSources: {
          originalData: 'Enhanced Fallback Data (Real Structure)',
          simulationData: 'Readiness Optimization Engine',
          attackVectors: 'Realistic Threat Vectors',
          assessmentType: 'Fallback with Real Data Structure'
        }
      };
    }
  };

  // ✅ FIXED: Business Intelligence loading with proper error handling
  const loadBusinessIntelligence = async (): Promise<void> => {
    try {
      setBusinessLoading(true);
      setBusinessError(null);
      setIsFallbackData(false);

      const assessmentId = integrationData?.assessmentId;
      if (assessmentId == null) {
        // No session to read — never substitute an id (finally clears loading).
        setBusinessError('No assessment selected.');
        return;
      }
      let data: BusinessIntelligenceData;

      // Reuse cached BI response from fetchRealAssessmentData to avoid duplicate API call
      if (cachedBiResponseRef.current) {
        const raw = cachedBiResponseRef.current;
        data = defensePlanningBusinessService.convertRawToBusinessData(raw, assessmentId.toString());
        cachedBiResponseRef.current = null; // Clear cache after use
      } else {
        // Fallback to fresh fetch if cache is empty (e.g. manual refresh)
        data = await defensePlanningBusinessService.getBusinessIntelligence(assessmentId.toString());
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setBusinessData(data);

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('❌ Defense Planning: Error loading business intelligence:', err);
      setBusinessError(`Could not load business intelligence: ${errorMessage}`);
      setIsFallbackData(true);

      // Fallback with conservative defaults — clearly not real data
      setBusinessData({
        assessmentId: integrationData?.assessmentId?.toString() || '',
        actualRevenue: {
          amount: 10000000,
          formatted: '$10.0M'
        },
        revenueAtRisk: {
          amount: 1000000,
          formatted: '$1.0M',
          calculation: {
            actualRevenue: 10000000,
            dailyRevenue: 27397,
            averageRiskScore: 59,
            recoveryDays: 20
          }
        },
        companyProfile: {
          name: integrationData?.companyName || 'Unknown Company',
          industry: integrationData?.industry || 'General',
          revenue: 10000000,
          employeeCount: 100
        }
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // ============ EVENT HANDLERS ============

  // const handleLogout = (): void => { // Unused for now
  //   console.log('🚪 Defense Planning: User logging out');
  //   window.location.href = '/';
  // };

  // const getAppConfig = (): AppConfig => { // Unused for now
  //   const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  //   
  //   if (isDevelopment) {
  //     return {
  //       compassUrl: 'http://localhost:5174',
  //       insightsDashboardUrl: 'http://localhost:5173',
  //       environment: 'development'
  //     };
  //     const baseUrl = window.location.origin;
  //     return {
  //       compassUrl: `${baseUrl}/compass`,
  //       insightsDashboardUrl: `${baseUrl}`,
  //       environment: 'production'
  //     };
  //   }
  // };

  // ============ ATTACK VECTOR HANDLERS ============

  const handleVectorSelect = (vector: AttackVectorWithControls): void => {
    console.log('🎯 Attack vector selected:', vector.name);
    setSelectedVector(vector);
  };

  // Memoize the simulation toggle callback to prevent infinite loops
  const handleSimulationToggle = useCallback(() => {
    setIntegrationData(prev => {
      if (!prev) {
        console.log('❌ handleSimulationToggle: No integrationData available');
        return null;
      }
      const currentSimulation = prev.isSimulation || false;
      const newSimulation = !currentSimulation;
      console.log('🎮 handleSimulationToggle: current:', currentSimulation, '→ new:', newSimulation);
      
      const newState = {
        ...prev,
        isSimulation: newSimulation
      };
      return newState;
    });
  }, []); // Remove dependency to prevent stale closure

  const handleSliderChange = (category: string, newValue: number): void => {
    // Use the current scores from integrationData, not initial scores
    const currentScores = integrationData?.currentScores || getInitialReadinessScores();
    const updatedScores = {
      ...currentScores,
      [category]: newValue
    };
    
    console.log(`🎮 Simulation: ${category} changed to ${newValue}`);
    console.log('📊 Updated scores:', updatedScores);
    
    if (integrationData) {
      setIntegrationData(prev => prev ? {
        ...prev,
        currentScores: updatedScores,
        isSimulation: true,
        lastSimulationUpdate: new Date().toISOString()
      } : null);
    }
  };

  // 🎯 NEW: Handle To-Be score updates (no feedback loops possible)
  const lastToBeUpdateRef = useRef<string>('');
  const handleToBeScoresUpdate = useCallback((newToBeScores: { [key: string]: number }): void => {
    const toBeScoresKey = JSON.stringify(newToBeScores);
    
    // Prevent duplicate updates
    if (toBeScoresKey === lastToBeUpdateRef.current) {
      console.log('⚠️ Preventing duplicate To-Be score update');
      return;
    }
    
    console.log('🎯 NEW TO-BE SCORES RECEIVED:', newToBeScores);
    console.log('🎯 As-Is scores (unchanged):', asIsScores);
    lastToBeUpdateRef.current = toBeScoresKey;
    
    // 🎯 ONLY update To-Be scores - As-Is scores never change
    setToBeScores(newToBeScores);
    
    // Also update integration data for backward compatibility
    // Don't automatically set isSimulation - let handleSimulationToggle control this
    setIntegrationData(prev => prev ? {
      ...prev,
      currentScores: newToBeScores,
      lastSimulationUpdate: new Date().toISOString()
    } : null);
    
    console.log('🎯 To-Be scores updated successfully');
  }, [asIsScores]);

  // ============ SIMULATION SAVE FUNCTIONS ============
  
  const saveSimulationScenario = async (scenarioName: string): Promise<void> => {
    if (!integrationData || !integrationData.isSimulation) {
      console.warn('⚠️ No simulation data to save');
      return;
    }

    try {
      const simulationData = {
        scenarioName,
        assessmentId: integrationData.assessmentId,
        userId: localStorage.getItem('userId'),
        originalScores: integrationData.originalScores,
        simulatedScores: integrationData.currentScores,
        createdAt: new Date().toISOString(),
        metadata: {
          companyName: integrationData.companyName,
          industry: integrationData.industry,
          persona: integrationData.persona
        }
      };

      // Save to simulation endpoint (NOT assessment endpoint)
      const response = await fetch(`${API_BASE_URL}/api/simulations/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(simulationData)
      });

      if (response.ok) {
        console.log('✅ Simulation scenario saved:', scenarioName);
        // Could show success message to user here
      } else {
        console.error('❌ Failed to save simulation scenario');
      }
    } catch (error) {
      console.error('❌ Error saving simulation:', error);
    }
  };

  const resetToOriginalData = (): void => {
    if (integrationData) {
      setIntegrationData(prev => prev ? {
        ...prev,
        currentScores: { ...prev.originalScores },
        isSimulation: false,
        lastSimulationUpdate: undefined
      } : null);
      console.log('🔄 Reset to original assessment data');
    }
  };

  // ============ UI DATA HELPERS ============

  const getSpiderChartData = (): SpiderChartData[] => {
    if (!integrationData) {
      return [];
    }
    
    // 🎯 FIXED: Use currentScores (To-Be scores) instead of integrationData.currentScores
    const current = currentScores || integrationData?.currentScores || {};
    const original = asIsScores || integrationData?.originalScores || {};
    
    return [
      {
        category: 'Governance',
        current: current.governance || 68,
        original: original.governance || 68,
        fullMark: 100
      },
      {
        category: 'Culture',
        current: current.culture || 72,
        original: original.culture || 72,
        fullMark: 100
      },
      {
        category: 'Budget',
        current: current.budget || 78,
        original: original.budget || 78,
        fullMark: 100
      },
      {
        category: 'Technical Debt',
        current: current.technical_debt || 30,
        original: original.technical_debt || 30,
        fullMark: 100
      },
      {
        category: 'Third Party',
        current: current.third_party || 65,
        original: original.third_party || 65,
        fullMark: 100
      },
      {
        category: 'People',
        current: current.people || 75,
        original: original.people || 75,
        fullMark: 100
      }
    ];
  };

  const getInitialReadinessScores = (): { [key: string]: number } => {
    if (integrationData?.currentScores) {
      return {
        governance: integrationData?.currentScores?.governance || 68,
        culture: integrationData?.currentScores?.culture || 72,
        budget: integrationData?.currentScores?.budget || 78,
        technical_debt: integrationData?.currentScores?.technical_debt || 30,
        third_party: integrationData?.currentScores?.third_party || 65,
        people: integrationData?.currentScores?.people || 75
      };
    }
    
    return {
      governance: 68, culture: 72, budget: 78,
      technical_debt: 30, third_party: 65, people: 75
    };
  };

  // ============ EXECUTIVE DATA FUNCTIONS ============

  const fetchExecutiveFinancialData = async (sessionId: string): Promise<void> => {
    try {
      console.log('💰 Fetching executive financial data for session:', sessionId);
      
      const response = await fetch(`${API_BASE_URL}/api/executive-data/financial-data/${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('💰 Executive financial data response:', result);
      
      if (result.success && result.financialData) {
        setExecutiveData({
          industry: result.financialData.industry,
          annualRevenue: result.financialData.annualRevenue,
          currentCyberBudget: result.financialData.currentCyberBudget
        });
        console.log('✅ Executive financial data loaded successfully');
      } else {
        // No financial data on record — annualRevenue is genuinely unmeasured, not $10M.
        // resolveAnnualRevenue() falls through to businessData, then to null.
        console.warn('⚠️ No executive financial data found — leaving annual revenue unmeasured');
        setExecutiveData({
          industry: 'Manufacturing',
          annualRevenue: null,
          currentCyberBudget: 500000
        });
      }
    } catch (error) {
      console.error('❌ Error fetching executive financial data:', error);
      setExecutiveData({
        industry: 'Manufacturing',
        annualRevenue: null,
        currentCyberBudget: 500000
      });
    }
  };

  // ============ INITIALIZATION EFFECT ============

  useEffect(() => {
    const initializeDefensePlanning = async () => {
      console.log('🔍 Defense Planning: Initializing with real data integration...');
      
      const urlParams = new URLSearchParams(window.location.search);
      let assessmentId = urlParams.get('assessmentId');
      const userId = urlParams.get('userId');
      const source = urlParams.get('source');
      
      // Check localStorage for stored sessionId if no URL assessmentId
      if (!assessmentId) {
        const storedSessionId = localStorage.getItem('currentSessionId');
        if (storedSessionId) {
          assessmentId = storedSessionId;
          console.log('📍 Friction Analysis: Using stored session ID:', assessmentId);
        }
      }
      
      console.log('🔗 URL parameters:', { assessmentId, userId, source });
      
      if (userId) {
        // setUser({ // Removed as unused
        //   id: userId,
        //   name: 'Business User',
        //   email: 'user@company.com',
        //   firstName: 'Business',
        //   lastName: 'User'
        // });
      }
      
      if (assessmentId) {
        console.log('✅ Loading real assessment data for ID:', assessmentId);
        
        try {
          const realIntegrationData = await loadRealAssessmentData(parseInt(assessmentId));
          setIntegrationData(realIntegrationData);
          // setIsIntegratedMode(true); // Removed as unused
          
          // 🎯 NEW: Set both As-Is and initial To-Be scores
          if (realIntegrationData?.currentScores && !asIsScores) {
            console.log('🎯 SETTING AS-IS SCORES FROM INTEGRATION DATA:', realIntegrationData.currentScores);
            setAsIsScores({ ...realIntegrationData.currentScores });
            setToBeScores({ ...realIntegrationData.currentScores }); // Initially same as As-Is
          }
          
          // 💰 NEW: Fetch executive financial data
          await fetchExecutiveFinancialData(assessmentId);
          
          console.log('🎯 Real assessment data integration complete');
          
        } catch (error) {
          console.error('❌ Error loading real assessment data:', error);
        }
      } else {
        console.log('🔍 No URL parameters - attempting to load user\'s assessment data...');
        
        // Try to load the current user's assessment data
        try {
          console.log('📋 Fetching latest assessment for authenticated user...');
          const response = await fetch(`${API_BASE_URL}/api/assessments/latest`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders()
            }
          });
          
          if (response.ok) {
            const latestAssessment = await response.json();
            console.log('✅ Found user assessment:', latestAssessment);
            
            if (latestAssessment.assessmentId) {
              const realIntegrationData = await loadRealAssessmentData(parseInt(latestAssessment.assessmentId));
              setIntegrationData(realIntegrationData);
              // setIsIntegratedMode(true); // Removed as unused
              
              // 🎯 NEW: Set both As-Is and initial To-Be scores
              if (realIntegrationData?.currentScores && !asIsScores) {
                console.log('🎯 SETTING AS-IS SCORES FROM USER DATA:', realIntegrationData.currentScores);
                setAsIsScores({ ...realIntegrationData.currentScores });
                setToBeScores({ ...realIntegrationData.currentScores }); // Initially same as As-Is
              }
              
              console.log('🎯 User assessment data loaded successfully');
            } else {
              console.log('⚠️ No assessment found - using demo mode');
            }
          } else {
            console.log('⚠️ Could not fetch user assessment - using demo mode');
          }
        } catch (error) {
          console.error('❌ Error loading user assessment:', error);
          console.log('⚠️ Falling back to demo mode');
        }
      }
      
      setIsLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    };
    
    initializeDefensePlanning();
  }, []);

  // Load business intelligence when integration data is ready
  useEffect(() => {
    if (integrationData && !businessData) {
      loadBusinessIntelligence();
    }
  }, [integrationData, businessData, loadBusinessIntelligence]);

  // ============ COMPUTED VALUES (MUST BE BEFORE ANY RETURNS) ============
  
  // 🎯 NEW: Current scores are now the To-Be scores (what gets displayed)
  // As-Is scores remain immutable, To-Be scores are modified by recommendations
  const currentScores = useMemo(() => {
    if (toBeScores) {
      return toBeScores; // Display the projected "To-Be" scores
    }
    if (asIsScores) {
      return asIsScores; // Fallback to As-Is if To-Be not set yet
    }
    return getInitialReadinessScores(); // Final fallback
  }, [toBeScores, asIsScores]);
  
  // Memoize spider data and zones with stable dependencies
  const spiderData = useMemo(() => {
    if (!integrationData) return [];
    return getSpiderChartData();
  }, [currentScores, asIsScores, integrationData?.assessmentId]); // 🎯 FIXED: Use correct dependencies
  
  const isSimulating = integrationData?.isSimulation || false;
  
  const readinessZones = useMemo(() => {
    return calculateReadinessZones(currentScores, businessData, integrationData?.industry);
  }, [currentScores, businessData, integrationData?.industry]);

  // ============ RENDER LOGIC ============

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>🔄 Loading Real Assessment Data...</h2>
        <p>Fetching assessment data and transforming for Defense Planning interface...</p>
      </div>
    );
  }

  // Authentication is now handled by ProtectedRoute - always show the studio interface
  
  console.log('🔍 Main render - currentScores:', currentScores);
  console.log('🔍 Main render - integrationData?.originalScores:', integrationData?.originalScores);
  console.log('🔍 Main render - isSimulating:', integrationData?.isSimulation);
  
  console.log('💰 Main render - readinessZones:', readinessZones);


  return (
    <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
      {/* Navigation handled by AppLayout wrapper */}
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--ra-navy)' }}>
          {/* NavigationHeader removed - using UnifiedNavigation sidebar only */}

          <div style={{
            padding: currentView === 'outside-pressures' ? '0' : '20px',
            fontFamily: 'Arial, sans-serif'
          }}>

        {/* How Risk Optimization Studio Works — collapsible module guide.
            Hidden on the full-bleed outside-pressures view to preserve its
            edge-to-edge layout. */}
        {currentView !== 'outside-pressures' && (
          <HowItWorksGuide
            moduleName="Risk Optimization Studio"
            steps={[
              { title: 'Friction Analysis', desc: 'Six readiness dimensions (Identify, Protect, Detect, Respond, Recover, Govern) plotted against industry-specific Goldilocks zones. Sliders let you simulate "what if I invest more here" and see the impact in real time.' },
              { title: 'Goldilocks Zones', desc: 'Each readiness dimension has an under-invest / right-sized / over-invest band calibrated to your industry and size. Above the band = diminishing returns. Below = high marginal value.' },
              { title: 'Financial Impact', desc: 'Translates readiness changes into dollars — revenue at risk, annual loss expectancy, breach cost, ROI on additional investment. Driven by your actual revenue + industry incident benchmarks.' },
              { title: 'Outside Pressures', desc: 'Macro signals (regulatory, geopolitical, supply chain, market) that bias your risk profile beyond what an internal assessment alone can measure. Updates as those signals move.' },
              { title: 'Board Reports', desc: 'Generate a Risk Optimization Studio PDF for the board: top exposures, recommended actions, investment scenarios, and the Friction Analysis snapshot. Reuse-friendly for quarterly governance reviews.' },
            ]}
            footnote="The sliders are non-destructive — adjust freely and reset to your real assessment baseline anytime. Generate the PDF when you want to share a specific scenario."
          />
        )}

        {/* Conditional Content Based on Current View */}
        {currentView === 'friction' ? (
          <>
            {/* Executive Summary Banner - Clean header like Business Intelligence */}
            <div style={{ 
              marginBottom: '30px',
              padding: '20px',
              backgroundColor: 'var(--ra-glass-bg)',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div>
                  <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '5px' }}>
                    Friction Analysis
                  </h1>
                  <p style={{ fontSize: '14px', color: 'var(--ra-text-secondary)' }}>
                    {String(integrationData?.companyName || 'Demo Company')} • {String(integrationData?.industry || 'Technology')} • Assessment {String(integrationData?.assessmentId || 'Demo')}
                  </p>
                </div>
                <div style={{ 
                  padding: '8px 16px', 
                  backgroundColor: isSimulating ? '#fef3c7' : '#d1fae5', 
                  borderRadius: '6px',
                  border: `1px solid ${isSimulating ? '#fde68a' : '#6ee7b7'}`
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: isSimulating ? '#92400e' : '#065f46' }}>
                    {isSimulating ? '🎮 Simulation Mode' : '📊 Live Data'}
                  </span>
                </div>
              </div>
              
              {/* Business Intelligence Status */}
              <BusinessIntelligenceStatus
                businessData={businessData}
                businessLoading={businessLoading}
                businessError={businessError}
                onRefresh={loadBusinessIntelligence}
                riskScore={integrationData?.originalOverallRisk}
                isFallbackData={isFallbackData}
              />
            </div>
            {/* Main Content - Three Column Layout: Spider Chart | Business Impact | Attack Vectors */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 400px 1fr',
            gap: '20px',
            alignItems: 'start'
          }}>
            
            {/* Left Column - Spider Chart */}
            <div>
              <SpiderChart 
                data={spiderData}
                isSimulating={isSimulating}
                title="📊 Readiness Spider Chart (Multi-Persona Assessment)"
              />
            </div>

            {/* Middle Column - Business Impact Analysis */}
            <div>
              <BusinessImpactAnalysis
                businessData={businessData}
                currentScores={currentScores}
                readinessZones={readinessZones}
                isSimulating={isSimulating}
                personaContext={{
                  persona: integrationData?.persona,
                  personaDisplayName: integrationData?.personaDisplayName,
                  assessmentId: integrationData?.assessmentId,
                  companyName: integrationData?.companyName,
                  industry: integrationData?.industry
                }}
              />
            </div>

            {/* Right Column - Attack Vectors */}
            <div>
              <AttackVectorsList
                attackVectors={integrationData?.attackVectors || []}
                selectedVector={selectedVector}
                industry={integrationData?.industry}
                currentScores={currentScores}
                isSimulating={isSimulating}
                onVectorSelect={handleVectorSelect}
              />
            </div>
          </div>
        </div>

        {/* ✅ Goldilocks Zones Component with Dual Score Display */}
        <GoldilocksZones
          readinessZones={readinessZones}
          businessData={businessData}
          asIsScores={asIsScores || undefined} // 🎯 NEW: Pass As-Is scores for display
          toBeScores={toBeScores || undefined} // 🎯 NEW: Pass To-Be scores for display
          isSimulating={isSimulating} // 🎯 NEW: Show dual display when simulating
          title="💰 Goldilocks ROI - Optimal Readiness Zones (Real Data)"
        />

        {/* Security Controls Removed - Interface includes Attack Vectors + Readiness Optimization */}

        {/* ✅ Recommendation-Driven Optimizer (Replaces Sliders) */}
        {/* Needs a real session — no id is substituted when the assessment is unknown. */}
        {integrationData?.assessmentId != null && (
        <RecommendationOptimizer
          assessmentId={integrationData.assessmentId.toString()}
          asIsScores={asIsScores || currentScores} // 🎯 NEW: Immutable baseline scores
          toBeScores={toBeScores || currentScores} // 🎯 NEW: Projected scores
          onToBeScoresUpdate={handleToBeScoresUpdate} // 🎯 NEW: Only update To-Be scores
          isSimulating={isSimulating}
          onSimulationToggle={handleSimulationToggle}
        />
        )}
        
        {/* Optional: Keep sliders for manual override (hidden by default) */}
        {false && (
          <ReadinessSliders
            currentScores={currentScores}
            originalScores={integrationData?.originalScores || {}}
            onSliderChange={handleSliderChange}
            title="🎛️ Manual Controls (Advanced)"
          />
        )}

        {/* ✅ Simulation Controls */}
        {integrationData?.isSimulation && (
          <div style={{
            marginTop: '20px',
            padding: '20px',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffeaa7'
          }}>
            <h3 style={{ color: '#856404', marginBottom: '15px', fontSize: '16px' }}>
              🎮 Simulation Mode Active
            </h3>
            <p style={{ color: '#856404', fontSize: '14px', marginBottom: '15px' }}>
              You are currently running a what-if scenario. Original assessment data is protected and unchanged.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const scenarioName = prompt('Enter a name for this simulation scenario:');
                  if (scenarioName) {
                    saveSimulationScenario(scenarioName);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                💾 Save Simulation
              </button>
              <button
                onClick={resetToOriginalData}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🔄 Reset to Original
              </button>
            </div>
          </div>
        )}

        {/* Footer Info with Real Data Sources */}
        <div style={{ 
          marginTop: '30px',
          padding: '15px', 
          backgroundColor: 'var(--ra-glass-bg)', 
          borderRadius: '8px',
          fontSize: '12px',
          color: 'var(--ra-text-secondary)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div><strong>Original Data:</strong> {integrationData?.dataSources?.originalData}</div>
            <div><strong>Simulation Data:</strong> {integrationData?.dataSources?.simulationData}</div>
            <div><strong>Assessment Type:</strong> {integrationData?.dataSources?.assessmentType}</div>
            <div><strong>Interface:</strong> Three-Column Layout: Spider Chart + Dynamic Business Impact + Attack Vectors</div>
          </div>
        </div>
          </>
        ) : currentView === 'outside-pressures' ? (
          /* Outside Pressures Analysis View */
          <OutsidePressuresCenter
            assessmentId={integrationData?.assessmentId?.toString() || localStorage.getItem('currentSessionId') || undefined}
          />
        ) : currentView === 'executive-simulation' ? (
          /* Executive Risk-Revenue Optimization Studio */
          (() => {
            console.log('🎯 RENDERING ExecutiveSimulationStudio with data:', {
              currentView,
              assessmentId: integrationData?.assessmentId?.toString() || localStorage.getItem('currentSessionId') || 'No session',
              companyData: {
                name: businessData?.companyProfile?.name || 'Your Company',
                industry: executiveData?.industry || businessData?.companyProfile?.industry || 'General',
                annualRevenue: resolveAnnualRevenue(executiveData, businessData),
                currentCyberBudget: executiveData?.currentCyberBudget || 0
              }
            });
            return (
              <ExecutiveSimulationStudio
                assessmentId={integrationData?.assessmentId?.toString() || localStorage.getItem('currentSessionId') || undefined}
                companyData={{
                  name: businessData?.companyProfile?.name || 'Your Company',
                  industry: executiveData?.industry || businessData?.companyProfile?.industry || 'General',
                  annualRevenue: resolveAnnualRevenue(executiveData, businessData),
                  currentCyberBudget: executiveData?.currentCyberBudget || 0
                }}
              />
            );
          })()
        ) : (
          /* Other views can be added here */
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <h2>View: {currentView}</h2>
            <p>This view is under development.</p>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;// trigger reload
